import { jenks } from "simple-statistics";
import { Feature, Map as OlMap } from "ol";
import Overlay from "ol/Overlay";
import { Point, LineString, Polygon, Circle, Geometry, MultiPolygon } from "ol/geom";
import MultiLineString from "ol/geom/MultiLineString";
import { Type } from "ol/geom/Geometry";
import VectorSource from "ol/source/Vector";
import { Vector as VectorLayer, Group as LayerGroup, Layer } from "ol/layer";
import { Fill, Stroke, Style, Text, Circle as CircleStyle, RegularShape, Icon } from "ol/style";
import { StyleLike } from "ol/style/Style";
import { getArea, getLength } from "ol/sphere";
import { Draw } from "ol/interaction";
import { GeoJSON } from "ol/format";
import { FeatureLike } from "ol/Feature";
import FillPattern from "ol-ext/style/FillPattern";
import unkinkPolygon from "@turf/unkink-polygon";
import { getLayer, getLayerGroup, LayerName, removeInteractions, removeOverlays } from "./olmap";
import { Coordinate } from "ol/coordinate";
import { fromLonLat, Projection, transform } from "ol/proj";
import { IsochroneFeature } from "../../components/map/sidesheet/analysis/DirectionAnalysis";
import icons from "../../img/icon/icons";

import { MapToolType } from "../../screens/MapScreen";
import { isConstructorDeclaration } from "typescript";

/**
 * 숫자에 1,000 단위 구분자를 추가하는 유틸리티 함수
 * 소수점이 있을 경우 소수점 아래 N자리까지 반올림 해 출력 | 250801
 * 정수는 소수점 없이 출력
 *
 * @param value 포맷팅할 숫자 값
 * @returns 구분자가 추가된 문자열
 */
const formatNumber = (value: number | string | undefined, decimal: number = 1): string => {
    /*
    if (value === undefined || value === null) return "";

    // 숫자를 문자열로 변환 (이미 문자열이면 그대로 사용)
    const numStr = typeof value === "number" ? value.toString() : value;

    // 소수점이 있는 경우와 없는 경우를 구분하여 처리
    const parts = numStr.split(".");

    // 정수 부분에 천 단위 구분자 추가
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // 소수점이 있으면 다시 합침
    return parts.length > 1 ? parts.join(".") : parts[0];
    */

    /** 소수점 추가하여 수정*/
    if (value === undefined || value === null || value === "") return "";

    // 숫자 변환
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) return "";

    // 아주 작은 수 처리
    const epsilonThreshold = 1e-6;
    const cleanedNum = Math.abs(num) < epsilonThreshold ? 0 : num;

    // 문자열로 변환
    const rounded = parseFloat(cleanedNum.toFixed(decimal)); // number로 확정
    let str = rounded.toString();
    const parts = str.split(".");

    // 정수부 쉼표 추가
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (parts.length > 1) {
        // 소수점 자르기만 (반올림 없이)
        const decimalTrimmed = parts[1].substring(0, decimal);
        return decimalTrimmed.length > 0 ? `${parts[0]}.${decimalTrimmed}` : parts[0];
    } else {
        return parts[0];
    }
    // return String(value);
};

// consts, enums ------------------------------------
const continuePolygonMsg = "면적을 그릴 지점을 클릭해주세요";
const continueLineMsg = "거리계산 할 지점을 클릭해주세요";

enum CalToolType {
    Point = -1,
    PolygonRing = 6,
    LineString = 10,
    Polygon,
    Circle,
}

enum ColorPreset {
    DARKBLUE,
    BLUE,
    PINK,
    WHITE,
    BLACK,
    PURPLE,
    TRANSPARENT,
}

enum RegionType {
    COUNTRY = "COUNTRY",
    CTPRVN = "CTPRVN",
    SIG = "SIG",
    EMD = "EMD",
    LI = "LI",
}

const accessibilityCondition = {
    MIN_POINTS: 2,
    MAX_POINTS: 5,
    BUCKETS: 10, // 백엔드 설정과 통일해야 함
    PROJECTION: "EPSG:4326",
} as const;

// styles --------------------------------
const colorMaker = (color: ColorPreset, opacity: number = 1) => {
    switch (color) {
        case ColorPreset.DARKBLUE:
            return `rgba(0, 77, 149, ${opacity})`;
        case ColorPreset.BLUE:
            return `rgba(0, 136, 255, ${opacity})`;
        case ColorPreset.PINK:
            return `rgba(255, 0, 102, ${opacity})`;
        case ColorPreset.BLACK:
            return `rgba(0, 0, 0, ${opacity})`;
        case ColorPreset.PURPLE:
            return `rgba(200, 55, 255, ${opacity})`;
        case ColorPreset.WHITE:
            return `rgba(255, 255, 255, ${opacity})`;
        case ColorPreset.TRANSPARENT:
            return "transparent";
    }
};

const blueCircleStyle = new CircleStyle({
    radius: 5,
    stroke: new Stroke({
        color: colorMaker(ColorPreset.BLUE),
    }),
    fill: new Fill({
        color: colorMaker(ColorPreset.WHITE),
    }),
});

const pinkCircleStyle = new CircleStyle({
    radius: 5,
    stroke: new Stroke({
        color: colorMaker(ColorPreset.PINK),
    }),
    fill: new Fill({
        color: colorMaker(ColorPreset.WHITE),
    }),
});

const purpleCircleStyle = new CircleStyle({
    radius: 5,
    stroke: new Stroke({
        color: colorMaker(ColorPreset.PURPLE),
    }),
    fill: new Fill({
        color: colorMaker(ColorPreset.WHITE),
    }),
});

const finishStyle = new Style({
    fill: new Fill({
        color: colorMaker(ColorPreset.BLUE, 0.3),
    }),
    stroke: new Stroke({
        color: colorMaker(ColorPreset.BLUE, 0.5),
        width: 2,
    }),
    image: blueCircleStyle.clone(),
});

const labelStyle = new Style({
    text: new Text({
        font: "12px NotoSansB",
        fill: new Fill({
            color: colorMaker(ColorPreset.BLUE),
        }),
        backgroundFill: new Fill({
            color: colorMaker(ColorPreset.WHITE),
        }),
        padding: [4, 8, 4, 8],
        textBaseline: "bottom",
        textAlign: "center",
        justify: "center",
        offsetY: -16,
        backgroundStroke: new Stroke({
            color: colorMaker(ColorPreset.BLUE),
            width: 2,
            lineCap: "round",
        }),
    }),
});

const tipStyle = new Style({
    text: new Text({
        font: "12px NotoSansB",
        fill: new Fill({
            color: colorMaker(ColorPreset.WHITE),
        }),
        backgroundFill: new Fill({
            color: colorMaker(ColorPreset.BLACK, 0.4),
        }),
        backgroundStroke: new Stroke({
            color: colorMaker(ColorPreset.WHITE),
            width: 2,
            lineCap: "round",
        }),
        padding: [4, 8, 4, 8],
        textAlign: "left",
        offsetX: 16,
    }),
});

const segmentStyle = new Style({
    text: new Text({
        font: "12px NotoSansB",
        fill: new Fill({
            color: colorMaker(ColorPreset.PINK),
        }),
        backgroundFill: new Fill({
            color: colorMaker(ColorPreset.WHITE),
        }),
        padding: [4, 8, 4, 8],
        textBaseline: "bottom",
        textAlign: "center",
        justify: "center",
        offsetY: -16,
        backgroundStroke: new Stroke({
            color: colorMaker(ColorPreset.PINK),
            width: 2,
            lineCap: "round",
        }),
    }),
    image: pinkCircleStyle.clone(),
});

const extentAnalysisStyle = new Style({
    fill: new Fill({
        color: colorMaker(ColorPreset.BLACK, 0.5),
    }),
    stroke: new Stroke({
        color: colorMaker(ColorPreset.BLUE, 0.5),
        width: 2,
    }),
});

const extentAnalysisPolyStyle = new Style({
    stroke: new Stroke({
        color: colorMaker(ColorPreset.BLUE, 0.5),
        width: 2,
    }),
    image: blueCircleStyle,
});

const extentAnalysisCircleStyle = new Style({
    stroke: new Stroke({
        color: colorMaker(ColorPreset.BLUE, 0.5),
        width: 2,
    }),
});

/**
 * 범위 분석 에서 사용되는 추가 레이어 스타일 생성 함수
 */
const extentAnalysisOutsideStyle = new Style({
    fill: new Fill({
        color: colorMaker(ColorPreset.BLACK, 0.4),
    }),
});

const choroplethStyle = new Style({
    stroke: new Stroke({
        color: "black",
        width: 1,
    }),
    fill: new Fill({
        color: "white",
    }),
});

/* Label은 div로 처리 */
// const choroplethLabelStyle = new Style({
//     text: new Text({
//         font: "14px NotoSansB",
//         fill: new Fill({
//             color: "red",
//         }),
//         backgroundFill: new Fill({
//             color: "rgba(255, 255, 255, 1)",
//         }),
//         backgroundStroke: new Stroke({
//             color: "red",
//             width: 3,
//             lineCap: "round",
//             lineJoin: "round",
//         }),
//         padding: [4, 8, 4, 8],
//     }),
// });

const choroplethUnLabelStyle = new Style({
    text: new Text({
        font: "14px NotoSansB",
        fill: new Fill({ color: "red" }),
        stroke: new Stroke({ color: "white", width: 3 }),
    }),
});

const regionStyle = new Style({
    stroke: new Stroke({
        color: "rgba(0, 136, 255, 0.5)",
        width: 2,
    }),
    fill: new Fill({
        color: "rgba(0, 136, 255, 0)",
    }),
});

const regionLabelStyle = new Style({
    text: new Text({
        font: "14px NotoSansB",
        fill: new Fill({
            // color: "blue",
            color: "rgb(0, 136, 255)",
        }),
        // stroke: new Stroke({
        //     color: "white", // 테두리 색상
        //     width: 4,       // 테두리 두께
        // }),
        backgroundFill: new Fill({
            color: "rgba(255, 255, 255, 1)",
        }),
        backgroundStroke: new Stroke({
            color: "rgb(0, 136, 255)",
        }),
        padding: [3, 2, 2, 3],
    }),
});

const chartStyle = new Style({
    stroke: new Stroke({
        color: "rgb(7, 32, 100)",
        width: 1,
    }),

    fill: new Fill({
        color: "rgba(139, 174, 240, 0.7)",
    }),

    zIndex: 2,
});

const accessibilityPolygonStyle = new Style({
    fill: new FillPattern({
        pattern: "hatch",
        size: 1,
        spacing: 5,
        angle: 30,
        color: "rgba(255, 0, 102, 0.3)",
        fill: new Fill({
            color: "rgba(255, 0, 102, 0.2)",
        }),
    }),
    stroke: new Stroke({
        color: "rgba(255, 0, 102, 1)",
    }),
});

const accessibilityRawPolygonStyle = new Style({
    fill: new Fill({
        color: "rgba(0, 136, 255, 0.1)",
    }),
    stroke: new Stroke({
        color: "rgba(0, 136, 255, 1)",
    }),
});

const accessibilityMarkerStyle = new Style({
    image: new Icon({
        src: (icons as any)["marker"],
        color: "rgb(0, 136, 255)",
        scale: 0.6,
    }),
});
// choropleth ---------------------------------------
const getRegionType = (regionCode: string, option?: "lower" | "upper") => {
    console.log(`[DBG] getRegionType: ${regionCode}, option: ${option}`);
    // 2026.01.16 | dslee | 방어코드 추가, regionCode null 일 때 undefined 반환
    if (!regionCode) return undefined; 
    switch (regionCode.length) {
        case 1:
            if (!option) return RegionType.COUNTRY;
            return option === "upper" ? undefined : RegionType.CTPRVN;
        case 2:
            if (!option) return RegionType.CTPRVN;
            return option === "upper" ? RegionType.COUNTRY : RegionType.SIG;
        case 5:
            if (!option) return RegionType.SIG;
            return option === "upper" ? RegionType.CTPRVN : RegionType.EMD;
        case 8:
            if (!option) return RegionType.EMD;
            return option === "upper" ? RegionType.SIG : RegionType.LI;
        case 10:
            if (!option) return RegionType.LI;
            return option === "upper" ? RegionType.EMD : undefined;
        default:
            return undefined;
    }
};

const getUpperRegionCode = (regionCode: string) => {
    switch (regionCode.length) {
        case 2:
            return "0";
        case 5:
            return regionCode.slice(0, 2);
        case 8:
            return regionCode.slice(0, 5);
        case 10:
            return regionCode.slice(0, 8);
        default:
            return undefined;
    }
};

const choroplethStyleFunction = (featureLike: FeatureLike) => {
    const feature = featureLike as Feature;
    const count = feature.get("count");
    const hover = feature.get("hover");
    const focus = feature.get("focus");
    const selected = feature.get("selected");
    const style = choroplethStyle.clone();
    // const labelStyle = choroplethLabelStyle.clone(); // Orgin
    // 250428 smyun | hover || selected 아닐 시 라벨 스타일 변경
    // const labelStyle = hover || selected ? choroplethLabelStyle.clone() : choroplethUnLabelStyle.clone();

    // hover | selected Label은 div로 처리(border-radius)
    const labelStyle = choroplethUnLabelStyle.clone();

    const numOfCls = feature.get("numOfCls");
    const cls = feature.get("class");

    // focus되지 않은 지역은 흐리게 표기
    if (!focus) {
        style.setFill(
            new Fill({
                color: "rgba(0, 0, 0, 0.2)",
            })
        );

        return style;
    }

    // focus된 지역의 색상 설정
    if (count && cls && numOfCls) {
        style.setFill(
            new Fill({
                color: getColorByClass(cls, numOfCls, 0),
            })
        );
    }

    // hover 또는 선택 시 테두리 강조
    if (hover || selected) {
        style.setStroke(
            new Stroke({
                color: "Crimson",
                width: 3,
            })
        );

        style.setZIndex(2);
    }

    // label
    const key = Object.keys(feature.getProperties()).filter((key) => key.endsWith("KOR_NM"))[0];
    if (key) {
        const label = feature.get(key);
        const labelGeometry = calLabelPosition(feature); // 라벨 위치 계산

        labelStyle.getText().setText(label); // hover시에도 count 나오도록 수정
        labelStyle.setZIndex(3);
        labelStyle.setGeometry(labelGeometry);
    }

    return [style, labelStyle];
};

const regionStyleFunction = (featureLike: FeatureLike) => {
    const feature = featureLike as Feature;
    const geometryLike = feature.getGeometry();

    const count = feature.get("count");
    const hover = feature.get("hover");
    const focus = feature.get("focus");
    const selected = feature.get("selected");
    const style = regionStyle.clone();
    const labelStyle = regionLabelStyle.clone();
    let regionCircleStyle = new Style({});

    const numOfCls = feature.get("numOfCls");
    const cls = feature.get("class");

    if (!focus) {
        style.setFill(
            new Fill({
                color: "rgba(0, 0, 0, 0.5)",
            })
        );

        return style;
    }

    if (hover || selected) {
        style.setStroke(
            new Stroke({
                // color: "blue",
                color: "rgb(0, 119, 255)",
                width: 3,
            })
        );

        style.setZIndex(2);
    }

    // Circle
    let interiorPoint;
    if (count !== undefined && count > 0 && cls !== undefined && numOfCls) {
        const minRadius = 10;
        const maxRadius = 50;
        const radius = minRadius + (maxRadius - minRadius) * (cls / (numOfCls > 1 ? numOfCls - 1 : 1)); // 비례식
        // const radius = Number.isFinite(rawRadius) ? rawRadius : 10;

        const key = Object.keys(feature.getProperties()).filter((key) => key.endsWith("KOR_NM"))[0];
        const regionLabel = feature.get(key);
        // 25.07.09 | Kim Young Jun | 1000 단위 구분자 포매팅
        const formattedCount = formatNumber(count);

        if (geometryLike?.getType() === "MultiPolygon") {
            const multi = geometryLike as MultiPolygon;
            const biggestPolygon = multi.getPolygons().sort((a, b) => b.getArea() - a.getArea())[0];
            interiorPoint = biggestPolygon.getInteriorPoint();
        } else {
            const polygon = geometryLike as Polygon;
            interiorPoint = polygon.getInteriorPoint();
        }

        regionCircleStyle = new Style({
            image: new CircleStyle({
                radius: radius,
                fill: new Fill({ color: "rgba(0, 119, 255, 0.5)" }),
                stroke: new Stroke({ color: "rgba(0, 119, 255, 1)", width: 1 }),
            }),
            text: new Text({
                // text: `${regionLabel}\n${count}`, // 표시할 텍스트
                fill: new Fill({ color: "rgba(0, 0, 0, 1)" }), // 텍스트 색상
                stroke: new Stroke({ color: "rgba(255, 255, 255, 1)", width: 3 }), // 텍스트 윤곽선
            }),
            zIndex: 4,
            geometry: interiorPoint,
        });

        regionCircleStyle.getText().setText(count > 0 ? `${regionLabel}\n${formattedCount}` : undefined);
    }

    // 마우스 호버
    // if (hover) {
    if (count == 0) {
        const key = Object.keys(feature.getProperties()).find((key) => key.endsWith("KOR_NM"));

        if (key) {
            const label = feature.get(key);
            labelStyle.getText().setText(label);
            let labelGeometry;

            if (geometryLike?.getType() === "MultiPolygon") {
                const geometry = geometryLike as MultiPolygon;
                const biggest = geometry.getPolygons().sort((poly1, poly2) => poly2.getArea() - poly1.getArea())[0];
                labelGeometry = biggest.getInteriorPoint();
            } else {
                const geometry = geometryLike as Polygon;
                labelGeometry = geometry.getInteriorPoint();
            }

            labelStyle.setGeometry(labelGeometry);
            labelStyle.setZIndex(3);
        }
    }

    return [style, labelStyle, regionCircleStyle];
    // return [style, regionCircleStyle]
};

const chartStyleFunction = (featureLike: FeatureLike) => {
    const feature = featureLike as Feature;

    const count = feature.get("count");
    const hover = feature.get("hover");
    const focus = feature.get("focus");
    const selected = feature.get("selected");
    const style = chartStyle.clone();
    // const labelStyle = chartLabelStyle.clone();

    const numOfCls = feature.get("numOfCls");
    const cls = feature.get("class");

    if (!focus) {
        style.setStroke(
            new Stroke({
                color: "rgba(0, 0, 0, 1)",
                width: 1,
            })
        );

        style.setFill(
            new Fill({
                color: "rgba(0, 0, 0, 0.3)",
            })
        );

        return style;
    }

    if (count && cls && numOfCls) {
        style.setStroke(
            new Stroke({
                color: "rgb(7, 32, 100)",
                width: 1,
            })
        );
        style.setFill(
            new Fill({
                color: "rgba(15, 142, 254, 0.7)",
            })
        );
        style.setZIndex(3);
    }

    if (hover || selected) {
        style.setStroke(
            new Stroke({
                color: "rgba(0, 0, 255, 1)",
                width: 2,
            })
        );
        style.setFill(
            new Fill({
                color: "rgba(200, 210, 255, 0.7)",
            })
        );
        style.setZIndex(4);
    }

    const key = Object.keys(feature.getProperties()).filter((key) => key.endsWith("KOR_NM"))[0];

    if (key && selected) {
        // const label = feature.get(key);
        // labelStyle.getText().setText(selected ? `${label}` : label);

        const geometryLike = feature.getGeometry();

        let labelGeometry;

        if (geometryLike?.getType() === "MultiPolygon") {
            const geometry = geometryLike as MultiPolygon;
            const biggest = geometry.getPolygons().sort((poly1, poly2) => poly2.getArea() - poly1.getArea())[0];
            labelGeometry = biggest.getInteriorPoint();
        } else if (geometryLike?.getType() === "Polygon") {
            const geometry = geometryLike as Polygon;
            labelGeometry = geometry.getInteriorPoint();
        }

        // if (labelGeometry) {
        //     labelStyle.getText().setText(label);
        //     labelStyle.setGeometry(labelGeometry);
        //     labelStyle.setZIndex(3);
        // }
    }

    return [style];
    // return [style, labelStyle];
};

const findFeatureInfo = (feature: Feature, layerNmae: LayerName) => {
    const key = Object.keys(feature.getProperties()).filter((key) => key.endsWith("KOR_NM"))[0];

    if (key) {
        const count = feature.get("count");
        const hover = feature.get("hover");
        const selected = feature.get("selected");

        const label = feature.get(key);
        // 25.07.09 | Kim Young Jun | 1000 단위 구분자 포매팅
        const formattedCount = formatNumber(count);

        let labelText;
        if (layerNmae === LayerName.CHART) {
            labelText = hover || selected ? `${label}` : label;
        } else {
            labelText = hover || selected ? `${label}\n${formattedCount}` : label;
        }
        const labelGeometry = calLabelPosition(feature); // 라벨 위치 계산

        return [labelText, labelGeometry as Point];
    }
    return ["", null];
};

// 라벨 위치 계산
const calLabelPosition = (feature: Feature) => {
    const geometryLike = feature.getGeometry();
    let labelGeometry;
    if (geometryLike?.getType() === "MultiPolygon") {
        const geometry = geometryLike as MultiPolygon;
        const biggest = geometry.getPolygons().sort((poly1, poly2) => poly2.getArea() - poly1.getArea())[0];
        labelGeometry = biggest.getInteriorPoint();
    } else {
        const geometry = geometryLike as Polygon;
        labelGeometry = geometry.getInteriorPoint();
    }
    return labelGeometry;
};

// smyun | overlay div 라벨 표현 함수
const updateOverlayLabel = (map: OlMap, feature?: Feature) => {
    const overlay = map
        .getOverlays()
        .getArray()
        .find((o) => o.get("name") === LayerName.CHOROPLETH) as Overlay | undefined;

    if (!overlay) return;

    const element = overlay.getElement();

    if (feature) {
        const [labelText, labelGeometry] = findFeatureInfo(feature as Feature, LayerName.CHOROPLETH);

        let labelGeometryCoords = labelGeometry.getCoordinates();

        overlay.setPosition(labelGeometryCoords);
        element!.textContent = labelText;
        element!.style.whiteSpace = "pre-line";
        element!.style.display = "block";
    } else {
        overlay.setPosition(undefined);
        element!.style.display = "none";
    }
};

const updateOverlayChartLabel = (map: OlMap, feature?: Feature) => {
    const overlay = map
        .getOverlays()
        .getArray()
        .find((o) => o.get("name") === LayerName.CHART) as Overlay | undefined;

    if (overlay) map.addOverlay(overlay);

    if (!overlay) return;

    const element = overlay.getElement();

    if (feature) {
        const [labelText, labelGeometry] = findFeatureInfo(feature as Feature, LayerName.CHART);

        let labelGeometryCoords = labelGeometry.getCoordinates();

        overlay.setPosition(labelGeometryCoords);

        element!.textContent = labelText;
        element!.style.whiteSpace = "pre-line";
        element!.style.display = "block";
    } else {
        overlay.setPosition(undefined);
        element!.style.display = "none";
    }
};

const createRegionLayer = (name: LayerName) => {
    let styleFn;

    if (name === LayerName.CHOROPLETH) {
        styleFn = choroplethStyleFunction;
    } else if (name === LayerName.REGION) {
        styleFn = regionStyleFunction;
    }

    const [CTPRVN, SIG, EMD] = [
        new VectorLayer({
            source: new VectorSource(),
            style: styleFn,
            zIndex: 1,
            properties: {
                name: RegionType.CTPRVN,
                group: name,
                min: 200,
            },
            declutter: name === "region" ? false : true, // 지도의 해상도와 관계없이 모든 요소 표출 여부(false: 다 보임 / ture: 공간에 따라 보이지 않음)
        }),
        new VectorLayer({
            source: new VectorSource(),
            style: styleFn,
            zIndex: 1,
            properties: {
                name: RegionType.SIG,
                group: name,
                min: 20,
                max: 200,
            },
            declutter: name === "region" ? false : true,
            visible: false,
        }),
        new VectorLayer({
            source: new VectorSource(),
            style: styleFn,
            zIndex: 1,
            properties: {
                name: RegionType.EMD,
                group: name,
                min: 0,
                max: 20,
            },
            declutter: name === "region" ? false : true,
            visible: false,
        }),
        // !@ gsshin (2026.01.28) : LI 레이어는 사용하지 않아 삭제함.
        // new VectorLayer({
        //     source: new VectorSource(),
        //     style: styleFn,
        //     zIndex: 1,
        //     properties: {
        //         name: RegionType.LI,
        //         group: name,
        //         min: 0,
        //         max: 10,
        //     },
        //     declutter: name === "region" ? false : true,
        //     visible: false,
        // }),
    ];

    const layerGroup = new LayerGroup({
        layers: [CTPRVN, SIG, EMD], // NOTE mjkang: LI 포함 원할 시 추가
        properties: {
            name: name,
        },
    });

    layerGroup.setVisible(false);
    return layerGroup;
};

const createChartRegionLayer = (name: LayerName) => {
    const [COUNTRY, CTPRVN, SIG, EMD] = [
        new VectorLayer({
            source: new VectorSource(),
            style: chartStyleFunction,
            zIndex: 1,
            properties: {
                name: RegionType.COUNTRY,
                group: name,
                min: 783.36,
            },
            declutter: name === "region" ? false : true,
        }),
        new VectorLayer({
            source: new VectorSource(),
            style: chartStyleFunction,
            zIndex: 1,
            properties: {
                name: RegionType.CTPRVN,
                group: name,
                min: 200,
                max: 750.36,
            },
            declutter: name === "region" ? false : true,
        }),
        new VectorLayer({
            source: new VectorSource(),
            style: chartStyleFunction,
            zIndex: 1,
            properties: {
                name: RegionType.SIG,
                group: name,
                min: 20,
                max: 200,
            },
            declutter: name === "region" ? false : true,
            visible: false,
        }),
        new VectorLayer({
            source: new VectorSource(),
            style: chartStyleFunction,
            zIndex: 1,
            properties: {
                name: RegionType.EMD,
                group: name,
                min: 10,
                max: 20,
            },
            declutter: name === "region" ? false : true,
            visible: false,
        }),        
        // !@ gsshin (2026.01.28) : LI 레이어는 사용하지 않아 삭제함.
        // new VectorLayer({
        //     source: new VectorSource(),
        //     style: chartStyleFunction,
        //     zIndex: 1,
        //     properties: {
        //         name: RegionType.LI,
        //         group: name,
        //         min: 0,
        //         max: 10,
        //     },
        //     declutter: name === "region" ? false : true,
        //     visible: false,
        // }),
    ];

    const layerGroup = new LayerGroup({
        layers: [COUNTRY, CTPRVN, SIG, EMD], // NOTE mjkang: LI 포함 원할 시 추가
        properties: {
            name: name,
        },
    });

    layerGroup.setVisible(false);
    return layerGroup;
};

// accessibility 접근성 분석 ----------------------------------------------
const stopAccessibility = (map: OlMap) => {
    const layer = getLayer(map, LayerName.ACCESSIBILITY);
    const source = layer?.getSource() as VectorSource;
    if (!source) return;

    source.clear();

    removeInteractions(map, LayerName.ACCESSIBILITY);
    removeOverlays(map, LayerName.ACCESSIBILITY);

    map.getViewport().style.cursor = "";
};

// !@#!-A1-STOP1. 오버랩 지도 종료 함수 - 오버랩 레이어 정리 및 클러스터 Z-Index 복원
const stopOverlapMap = (map: OlMap) => {
    const layerGroup = getLayerGroup(map, LayerName.OVERLAP);
    const clusterLayer = getLayer(map, LayerName.CLUSTER);
    clusterLayer?.setZIndex(1);
    layerGroup?.getLayers().clear();
    layerGroup?.setVisible(false);
};

// Direction Route ---------------------------------------------
// 소요시간 계산 (밀리초 -> 분/초)
const msToMinutes = (ms: number): string => {
    const seconds = Math.floor(ms / 1000); // 밀리초를 초로 변환
    const hours = Math.floor(seconds / 3600); // 1시간 = 3600초
    const minutes = Math.floor((seconds % 3600) / 60); // 남은 초에서 분을 계산
    const remainingSeconds = seconds % 60; // 나머지 초를 계산

    return `${minutes}분 ${remainingSeconds}초`;
};

// 거리계산
const distanceMToKm = (meters: number): string => {
    if (meters >= 1000) {
        // 1000미터 이상이면 킬로미터로 변환
        const kilometers = meters / 1000;
        return `${kilometers.toFixed(2)}km`; // 소수점 둘째 자리까지 제한
    } else {
        // 1000미터 미만이면 미터로 표시
        return `${meters}m`;
    }
};

const drawRouteMultiLine = (map: OlMap, routeData: any, startPoint: any, endPoint: any) => {
    if (!map) return;

    // 1. 레이어 그룹과 라우트 레이어 가져오기
    const layerGroup = getLayerGroup(map, LayerName.DIRECTION);
    if (!layerGroup) return;

    let routeLayer = getLayer(layerGroup, LayerName.ROUTE);
    let source: VectorSource;

    if (routeLayer) {
        source = routeLayer.getSource() as VectorSource;
    } else {
        source = new VectorSource();
        routeLayer = new VectorLayer({
            source: source,
            style: null, // feature 개별 스타일
        });
        routeLayer.set("name", LayerName.ROUTE);
        layerGroup.getLayers().push(routeLayer); // 레이어 그룹에 추가
    }

    // 2. 경로 데이터 변환(EPSG:5179로 좌표계 변환)
    const coordList = routeData.paths[0].points.coordinates;
    const transformed = coordList.map((coord: Coordinate) => transform(coord, "EPSG:4326", "EPSG:5179"));

    // 3. MultiLineString 생성 및 Feature 생성
    const multiLineGeom = new MultiLineString([transformed]);
    const multiLineFeature = new Feature({
        geometry: multiLineGeom,
    });

    // 4. 라인 스타일 지정
    multiLineFeature.setStyle(
        new Style({
            stroke: new Stroke({
                color: "#ff0066",
                width: 3,
            }),
        })
    );

    // 5. Source에 Feature 추가
    source.addFeature(multiLineFeature);

    // 6. 시작점-경로 출발점 / 도착점-경로 끝점 대시라인 추가
    const firstCoord = transformed[0];
    const lastCoord = transformed[transformed.length - 1];
    const transformedStartPoint = transform(startPoint, "EPSG:4326", "EPSG:5179");
    const transformedEndPoint = transform(endPoint, "EPSG:4326", "EPSG:5179");

    const dashedLineStyle = new Style({
        stroke: new Stroke({
            color: "#818181", // 점선 색상
            width: 3,
            lineDash: [5, 10], // 실선 5px, 공백 10px
        }),
    });

    const dashedLineStart = new Feature({
        geometry: new LineString([transformedStartPoint, firstCoord]),
    });
    dashedLineStart.setStyle(dashedLineStyle);
    source.addFeature(dashedLineStart);

    const dashedLineEnd = new Feature({
        geometry: new LineString([lastCoord, transformedEndPoint]),
    });
    dashedLineEnd.setStyle(dashedLineStyle);
    source.addFeature(dashedLineEnd);

    // 7. 지도가 해당 라인을 볼 수 있게 fit 시킴
    // map.getView().fit(multiLineGeom.getExtent(), {
    //     padding: [50, 50, 50, 50],
    //     duration: 500,
    // });

    // 이동거리, 소요시간 툴팁
    const duration = msToMinutes(routeData.paths[0].time);
    const distance = distanceMToKm(routeData.paths[0].distance);

    // 툴팁의 HTML 내용
    const tooltipContent = `
        <div style="
            background-color: rgba(0, 0, 0, 0.5);
            color: white;
            padding: 8px 12px;
            border-radius: 10px;
            font-size: 14px;
            line-height: 1.5;
            width: 135px;
        ">
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;"
            >
                <strong>소요 시간: </strong><span>${duration}</span>
            </div>
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;"
            >
                <strong>이동 거리: </strong><span>${distance}</span>
            </div>
        </div>
    `;

    // 툴팁 위치 1. 범위 중 중앙좌표
    // const bbox = routeData.paths[0].bbox
    // const centerLon = (bbox[0] + bbox[2]) / 2;
    // const centerLat = (bbox[1] + bbox[3]) / 2;

    // const centerCoord = [centerLon, centerLat];
    // const transformedCenterCoord = transform(centerCoord, 'EPSG:4326', 'EPSG:5179');

    // 툴팁 위치 2. 경로 중 중앙 좌표
    const centerCoord = transformed[Math.floor(transformed.length / 2)];
    const adjustedCenterCoord = [centerCoord[0], centerCoord[1]];

    mapTooltip(map, tooltipContent, adjustedCenterCoord);
};

// 지도 툴팁
const mapTooltip = (map: OlMap, tooltipContent: any, position: any) => {
    // 툴팁 엘리먼트 생성
    const tooltipElement = document.createElement("div");
    tooltipElement.className = "ol-tooltip";
    tooltipElement.innerHTML = tooltipContent;
    // tooltipElement.style.display = 'none';

    // 툴팁 오버레이 생성
    const tooltipOverlay = new Overlay({
        element: tooltipElement,
        positioning: "bottom-center",
        stopEvent: false,
    });

    // 지도에 추가
    map.addOverlay(tooltipOverlay);
    tooltipOverlay.setPosition(position);

    // 새 오버레이 저장
    map.set("routeTooltipOverlay", tooltipOverlay);
};

// 툴팁 클리어
const clearTooltip = (map: OlMap) => {
    // 기존 툴팁 오버레이 제거
    const existingTooltip = map.get("routeTooltipOverlay");
    if (existingTooltip) {
        map.removeOverlay(existingTooltip);

        const element = existingTooltip.getElement();
        if (element && element.parentNode) {
            element.parentNode.removeChild(element); // DOM에서도 제거
        }
    }
};

// calculate ----------------------------------------------------

const getCircleRadius = (value: number, min: number, max: number) => {
    // 25.07.11 | Kim Young Jun | 모바일 뷰 대응 반경 값 조정
    const defaultMinRadius = 20;
    const defaultMaxRadius = 120;

    // 화면 너비에 따라 반경 값 조정
    let minRadius = defaultMinRadius;
    let maxRadius = defaultMaxRadius;

    // 현재 화면 너비 확인
    const screenWidth = window.innerWidth;

    if (screenWidth <= 520) {
        minRadius = 10;
        maxRadius = 70;
    } else if (screenWidth <= 780) {
        minRadius = 15;
        maxRadius = 80;
    }

    if (max === min) return minRadius;

    const ratio = (value - min) / (max - min);
    const invertedRatio = 1 - ratio;

    return minRadius + (maxRadius - minRadius) * invertedRatio;
};

const getColorByClass = (cls: number, numOfCls: number, type: number) => {
    if (type === 0) {
        const calculated = Math.round(255 * (1 - Number(cls) / Number(numOfCls)));

        return `rgb(255, ${calculated}, ${calculated})`;
    } else {
        return "black";
    }
};

const getColorByUpperLower = (cls: number, numOfCls: number, type: number) => {
    if (type === 0 && numOfCls > 0) {
        return "rgba(0, 126, 237, 0.6)";
    } else {
        return "black";
    }
};

const selectDrawType = (calToolType: number) => {
    switch (calToolType) {
        case CalToolType.LineString:
            return "LineString";
        case CalToolType.Polygon:
        case CalToolType.PolygonRing:
            return "Polygon";
        case CalToolType.Circle:
            return "Circle";
        default:
            return "Point";
    }
};

const stopMeasure = (map: OlMap) => {
    const name = LayerName.CALCULATE;
    const calculateLayer = getLayer(map, name);

    if (calculateLayer) {
        const source = calculateLayer.getSource() as VectorSource;
        source.clear();
        calculateLayer.setVisible(false);
    }

    removeInteractions(map, name);
};

/**
 * 거리 포맷 함수
 *
 * @author cjs
 * @since 2024-12-26 16:01:04
 * @param props length
 * @props length
 * @returns output
 */

const formatLength = (length: number) => {
    let output;
    const mileConst = 0.621371; // 1 km = 0.621371 miles
    if (length > 100) {
        output = [
            `${(length / 1000).toFixed(2)} km`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "12px NotoSansB",
            "\n",
            "",
            `(${((length * mileConst) / 1000).toFixed(2)} miles)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
        ];
    } else {
        output = [
            `${length.toFixed(2)} m`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "12px NotoSansB",
            "\n",
            "",
            `(${((length * mileConst) / 1000).toFixed(2)} miles)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
        ];
    }
    return output;
};

/**
 * 면적 포맷 함수
 *
 * @author cjs
 * @since 2024-12-26 16:01:04
 * @param props polygon
 * @props polygon
 * @returns output
 */

const formatArea = (polygon: Feature<Geometry>) => {
    let geojsonFormat = new GeoJSON();
    let turfPolygon = geojsonFormat.writeFeatureObject(polygon);
    let coordinates = (turfPolygon.geometry as { type: "Polygon"; coordinates: number[][][] }).coordinates[0];

    let index = coordinates.length - 2;
    // 좌표 중복 제거
    if (
        index > 0 &&
        ((coordinates[index][0] === coordinates[index - 1][0] && coordinates[index][1] === coordinates[index - 1][1]) ||
            (coordinates[0][0] === coordinates[index][0] && coordinates[0][1] === coordinates[index][1]))
    ) {
        coordinates.splice(index, 1);
    }

    coordinates = coordinates.filter((coor, index, self) => index === self.findIndex((c) => c[0] === coor[0] && c[1] === coor[1]));

    let area = 0;
    let unkinkedFeatures: any[] = [];
    if (coordinates.length > 3) {
        let unkinked = unkinkPolygon(turfPolygon as any);
        unkinkedFeatures = geojsonFormat.readFeatures(unkinked);
    } else {
        area += getArea(polygon.getGeometry() as Polygon);
    }
    unkinkedFeatures.forEach(function (feature) {
        area += getArea(feature.getGeometry());
    });
    let output;
    let pyeong = 0.3025;
    let ha = 10000;
    if (area > 100000) {
        output = [
            `${Math.round((area / 1000000) * 100) / 100} km\xB2`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "12px NotoSansB",
            "\n",
            "",
            `(${Math.round((area / ha) * 100) / 100} ha)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
            "\n",
            "",
            `(${Math.round(area * pyeong * 100) / 100} 평)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
        ];
    } else {
        output = [
            `${Math.round(area * 100) / 100} m\xB2`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "12px NotoSansB",
            "\n",
            "",
            `(${Math.round((area / ha) * 100) / 100} ha)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
            "\n",
            "",
            `(${Math.round(area * pyeong * 100) / 100} 평)`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            "10px NotoSansB",
        ];
    }
    return output;
};

/**
 * 드로잉 스타일 함수
 *
 * @author cjs
 * @since 2024-12-26 16:01:04
 * @param props draw, mapObj, feature, drawType, tip
 * @props draw 드로잉 객체
 *        mapObj 지도 객체
 *        feature 피처 객체
 *        drawType 드로잉 타입
 *        tip 팁 메시지
 * @returns styles
 */

const styleFunction = (draw: Draw, mapObj: OlMap, feature: Feature, drawType: Type | null, tip: string | null, isFinish: boolean) => {
    let styles: Style[] = [];

    const segmentStyles: Style[] = [];
    const segmentLineStyles: Style[] = [];
    const geometry = feature.getGeometry() as Geometry;
    const type = geometry.getType();

    let point: Point | undefined;
    let label: string[] | undefined;
    let line: LineString | undefined;
    let startPoint: Point | undefined;

    if (!drawType || drawType === type || type === "Point") {
        if (drawType === "Polygon") {
            let finishPolygonStyle = finishStyle.clone();
            if (isFinish) {
                if (tip === "extentAnalysisEnd") {
                    finishPolygonStyle.getFill().setColor(colorMaker(ColorPreset.BLUE, 0));
                } else {
                    finishPolygonStyle.getFill().setColor(colorMaker(ColorPreset.BLUE, 0.5));
                }
                finishPolygonStyle.getStroke().setColor(colorMaker(ColorPreset.BLUE));
            }
            styles.push(finishPolygonStyle);
        } else if (drawType === "LineString") {
            let finishLineStyle = finishStyle.clone();
            isFinish
                ? finishLineStyle.getStroke().setColor(colorMaker(ColorPreset.PINK))
                : finishLineStyle.getStroke().setColor(colorMaker(ColorPreset.PINK, 0.5));
            finishLineStyle.setImage(pinkCircleStyle.clone());
            styles.push(finishLineStyle);
        } else if (drawType === "Circle") {
            let finishCircleStyle = finishStyle.clone();
            isFinish
                ? finishCircleStyle.getStroke().setColor(colorMaker(ColorPreset.PURPLE))
                : finishCircleStyle.getStroke().setColor(colorMaker(ColorPreset.PURPLE, 0.5));
            finishCircleStyle.setImage(purpleCircleStyle.clone());
            styles.push(finishCircleStyle);
        }

        if (drawType === "Polygon" && type === "Polygon" && geometry instanceof Polygon) {
            point = new Point(geometry.getInteriorPoint().getCoordinates());
            label = formatArea(feature);
            line = new LineString(geometry.getCoordinates()[0]);
        } else if (drawType === "LineString" && type === "LineString" && geometry instanceof LineString) {
            startPoint = new Point(geometry.getFirstCoordinate());
            point = new Point(geometry.getLastCoordinate());
            line = geometry;
        }
    } else {
        if (drawType === "Circle" && geometry instanceof LineString) {
            // geometry type = LineString
            const center = geometry.getFirstCoordinate(); // 원 중심
            // 두 점 사이의 실제 거리를 계산
            const radius = geometry.getLength(); // 두 점 사이의 실제 거리

            let end;
            if (isFinish) {
                end = geometry.getLastCoordinate();
            }

            // Center Point
            const centerPointStyle = new Style({
                geometry: new Point(center),
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({ color: colorMaker(ColorPreset.WHITE) }),
                    stroke: new Stroke({
                        color: isFinish ? colorMaker(ColorPreset.PURPLE) : colorMaker(ColorPreset.PURPLE, 0.5),
                        width: 1,
                    }),
                }),
            });

            if (end) {
                const endPointStyle = new Style({
                    geometry: new Point(end),
                    image: new CircleStyle({
                        radius: 5,
                        fill: new Fill({ color: colorMaker(ColorPreset.WHITE) }),
                        stroke: new Stroke({
                            color: isFinish ? colorMaker(ColorPreset.PURPLE) : colorMaker(ColorPreset.PURPLE, 0.5),
                            width: 1,
                        }),
                    }),
                });

                if (tip != "extentAnalysisEnd") {
                    styles.push(endPointStyle);
                }
            }

            // if(tip != "extentAnalysisEnd"){
            //     styles.push(centerPointStyle);
            // }
            styles.push(centerPointStyle);

            // Radial Line & Circle
            if (radius > 0) {
                // label 위치
                // label = tip != "extentAnalysisEnd" ? formatLength(getLength(geometry)) : undefined; // 반경을 sphere 거리로 계산
                label = formatLength(getLength(geometry));
                point = new Point(geometry.getCoordinateAt(0.5));

                const radiusLine = new Style({
                    geometry: geometry,
                    stroke: new Stroke({
                        color: isFinish ? colorMaker(ColorPreset.PURPLE) : colorMaker(ColorPreset.PURPLE, 0.5),
                        width: 2,
                    }),
                });

                // if(tip != "extentAnalysisEnd"){
                //     styles.push(radiusLine);
                // }

                styles.push(radiusLine);

                const circleStyle = new Style({
                    geometry: new Circle(center, radius),
                    stroke: new Stroke({
                        color: isFinish ? colorMaker(ColorPreset.PURPLE) : colorMaker(ColorPreset.PURPLE, 0.5),
                        width: 2,
                    }),
                    fill: new Fill({
                        color:
                            isFinish && tip == "extentAnalysisEnd"
                                ? colorMaker(ColorPreset.PURPLE, 0)
                                : isFinish
                                ? colorMaker(ColorPreset.PURPLE, 0.3)
                                : colorMaker(ColorPreset.PURPLE, 0.1),
                    }),
                });
                styles.push(circleStyle);
                mapObj.once("click", () => {
                    draw.finishDrawing();
                });
            }
        }
    }

    if (line) {
        let preLength = 0;
        let count = 0;

        line.forEachSegment((a, b) => {
            const segment = new LineString([a, b]);
            let label;

            if (drawType === "LineString") {
                let length;
                length = getLength(segment) + preLength;
                preLength = length;
                label = formatLength(length);
            }

            if (segmentStyles.length - 1 < count) {
                const temp = segmentStyle.clone();
                if (drawType === "Polygon") {
                    temp.setImage(blueCircleStyle.clone());
                }
                segmentStyles.push(temp);
            }

            if (drawType === "LineString") {
                segmentStyles[count].getText().setText(label);
            }

            const segmentPoint = new Point(segment.getCoordinateAt(1));

            segmentStyles[count].setGeometry(segmentPoint);
            segmentLineStyles.push(
                new Style({
                    geometry: new LineString([a, b]),
                    stroke: new Stroke({
                        color: drawType === "LineString" ? colorMaker(ColorPreset.PINK) : colorMaker(ColorPreset.BLUE),
                        width: 2,
                    }),
                })
            );

            styles.push(segmentStyles[count]);

            if (count !== 0) {
                if (drawType === "LineString") {
                    styles.push(segmentLineStyles[count - 1]);
                } else if (drawType === "Polygon" && count > 1) {
                    styles.push(segmentLineStyles[count - 2]);
                }
            }
            count++;
        });
    }

    if (startPoint) {
        const startPointStyle = segmentStyle.clone();
        startPointStyle.getText().setOffsetY(16);
        startPointStyle.getText().setTextBaseline("top");
        startPointStyle.setGeometry(startPoint as Point);
        startPointStyle.getText().setText("시작");
        styles.push(startPointStyle);
    }

    if (label && drawType === "Polygon") {
        let polygonLabelStyle = labelStyle.clone();
        polygonLabelStyle.setGeometry(point as Point);
        polygonLabelStyle.getText().setText(label);
        styles.push(polygonLabelStyle);
    } else if (label && drawType === "LineString") {
        let lineLabelStyle = labelStyle.clone();
        lineLabelStyle.getText().getFill().setColor(colorMaker(ColorPreset.PINK));
        lineLabelStyle.getText().getBackgroundStroke().setColor(colorMaker(ColorPreset.PINK));
        lineLabelStyle.setImage(pinkCircleStyle.clone());
        lineLabelStyle.setGeometry(point as Point);
        lineLabelStyle.getText().setText(label);
        styles.push(lineLabelStyle);
    } else if (label && drawType === "Circle") {
        let circleLabelStyle = labelStyle.clone();
        circleLabelStyle.getText().getFill().setColor(colorMaker(ColorPreset.PURPLE));
        circleLabelStyle.getText().getBackgroundStroke().setColor(colorMaker(ColorPreset.PURPLE));
        circleLabelStyle.setGeometry(point as Point);
        circleLabelStyle.getText().setText(label);
        styles.push(circleLabelStyle);
    }

    if (tip && type === "Point") {
        tipStyle.getText().setText(tip);
        styles.push(tipStyle);
    }

    return styles;
};

/**
 * 드로잉 툴 활성화 함수
 *
 * @author cjs
 * @since 2024-12-26 16:01:04
 * @param props mapObj, type
 * @props map {{@link OlMap}} 지도 객체
 * @props type {{@link Number}} 드로잉 타입
 * @returns draw {{@link Draw}}
 */
const startMeasure = (map: OlMap, type: number, extentTool: string, tool: string) => {
    const name = LayerName.CALCULATE;
    const layer = getLayer(map, name);
    const source = layer?.getSource() as VectorSource;

    if (!layer || !source) return;

    const drawType = selectDrawType(type);
    let tip = "시작점을 클릭해주세요";

    const newDraw = new Draw({
        source: source,
        type: type === CalToolType.Circle ? "LineString" : drawType,
        maxPoints: type === CalToolType.Circle ? 2 : undefined,
        geometryFunction: undefined,
        style: (feature): Style[] => {
            return styleFunction(newDraw, map, feature as Feature, drawType, tip, false);
        },
    });

    newDraw.setProperties({ name: name });

    newDraw.on("drawstart", () => {
        source.clear();
        tip = drawType === "Polygon" ? continuePolygonMsg : extentTool == "radius" || extentTool == "distance" ? continueLineMsg : "";
    });

    newDraw.on("drawend", function (evt) {
        tip = tool == "analysis" ? "extentAnalysisEnd" : "시작점을 클릭해주세요";

        const styles = styleFunction(newDraw, map, evt.feature as Feature, drawType, tip, true);
        evt.feature.setStyle(styles);
    });

    map.addInteraction(newDraw);
    layer.setVisible(true);

    return newDraw;
};

// direction ---------------------------
// FIXME smyun 레이어 표현 수정 예정
const createDirectionLayer = (name: LayerName) => {
    const startPoint = new VectorLayer({
        source: new VectorSource(),
        properties: {
            name: LayerName.STARTPOINT,
        },
    });

    const isochrone = new VectorLayer({
        source: new VectorSource(),
        properties: {
            name: LayerName.ISOCHORNE,
        },
        style: new Style({
            fill: new Fill({
                color: "rgba(0, 77, 149, 0.2)",
            }),
            stroke: new Stroke({
                color: "rgba(0,77,149,0.5)",
                width: 3,
            }),
        }),
    });
    const route = new VectorLayer({
        source: new VectorSource(),
        properties: {
            name: LayerName.ROUTE,
        },
        style: new Style({
            fill: new Fill({
                color: "rgba(0, 77, 149, 0.3)",
            }),
            stroke: new Stroke({
                color: "rgba(0,77,149,0.5)",
                width: 3,
            }),
            image: new CircleStyle({
                radius: 5,
                stroke: new Stroke({
                    color: "white",
                    width: 2,
                }),
                fill: new Fill({
                    color: "rgba(0,77,149,0.5)",
                }),
            }),
        }),
    });

    const layerGroup = new LayerGroup({
        layers: [isochrone, route, startPoint],
        properties: {
            name: name,
        },
    });

    layerGroup.setVisible(false);
    return layerGroup;
};

const directionStyleFunction = (draw: Draw, mapObj: OlMap, feature: FeatureLike, tip: string, isFin: Boolean) => {
    let styles: Style[] = [];
    const startPoint = feature.getGeometry() as Point;
    let label: string | undefined; // 시작, 출발, 도착

    // 1. startPoint
    // 2. isochrone
    // 3. route

    // Direction Styles
    const drawingPointerStyle = new Style({
        // draw point style
        fill: new Fill({
            color: "rgba(0, 77, 149, 0.5)",
        }),
        stroke: new Stroke({
            color: "rgba(0,77,149,0.5)",
            width: 3,
        }),
        image: new CircleStyle({
            radius: 5,
            // stroke: new Stroke({
            //     color: "white",
            //     width: 2,
            // }),
            fill: new Fill({
                color: "rgba(0,77,149,0.5)",
            }),
        }),
    });
    const pointMarkerStyle = new Style({
        fill: new Fill({
            color: colorMaker(ColorPreset.DARKBLUE),
        }),
        image: new CircleStyle({
            radius: 10,
            fill: new Fill({
                color: colorMaker(ColorPreset.DARKBLUE),
            }),
        }),
    });
    const tailStyle = new Style({
        image: new RegularShape({
            radius: 10,
            points: 3,
            angle: Math.PI,
            displacement: [0, -7],
            fill: new Fill({
                color: colorMaker(ColorPreset.DARKBLUE),
            }),
        }),
    });

    const labelStyle = new Style({
        text: new Text({
            font: "12px NotoSansB",
            fill: new Fill({
                color: colorMaker(ColorPreset.DARKBLUE),
            }),
            backgroundFill: new Fill({
                color: colorMaker(ColorPreset.WHITE),
            }),
            backgroundStroke: new Stroke({
                color: colorMaker(ColorPreset.DARKBLUE),
                width: 2,
                lineCap: "round",
            }),
            padding: [4, 8, 4, 8],
            textAlign: "left",
            text: "시작",
            offsetX: -10,
            offsetY: 30,
        }),
    });

    // tipStyle은 그대로 이용
    const modifystyle = new Style({});

    styles.push(drawingPointerStyle);
    styles.push(modifystyle);

    if (feature && startPoint instanceof Point) {
        // click 시 종료
        mapObj.once("click", () => {
            draw.finishDrawing();
        });

        // dragEnd시 동작
        if (isFin) {
            // "drawend"시 styles
            tip = ""; //tip 비워짐
            label = "시작";

            // directionStyleFunction_point 마커 모양 수정
            styles.push(tailStyle);
            styles.push(pointMarkerStyle);
            styles.push(labelStyle);
            styles.push(drawingPointerStyle);
        }
    }

    if (tip) {
        tipStyle.getText().setText(tip);
        styles.push(tipStyle);
    }

    return styles;
};

/**
 * <Polygon 좌표값으로 x,y 값 가공하기>
 * 작업내용 1 xyz -> xy | 2. 좌표 변경
 * @param isochroneFeature graphHopper isochrone 결과 중 polygons[0] 객체
 *        mapProj 좌표계
 * @returns [[[x,y]], [[x,y], ...]]
 */
const getCoordinatesFromIsochrone = (isochroneFeature: IsochroneFeature, mapProj: Projection): Coordinate[][] => {
    const coords = isochroneFeature.geometry.coordinates; //number[][][]
    const transCoords = coords.map((ring) => ring.map(([lon, lat]) => fromLonLat([lon, lat], mapProj))); // [[[x,y]], [[x,y]]]

    return transCoords;
};

const createCoordinatesParameter = (flatCoords: number[][]): string => {
    const result = flatCoords.map((coord) => coord.join(",")).join(" ");
    // console.log(`flatCoords: ${result}`); // CHECK
    return result;
};

const createGmlPolygonString = (coords: number[][]): string => {
    const coordParameter = createCoordinatesParameter(coords);
    return `
        <gml:outerBoundaryIs>
          <gml:LinearRing>
            <gml:coordinates>${coordParameter}</gml:coordinates>
          </gml:LinearRing>
        </gml:outerBoundaryIs>
    `.trim();
};

/**
 * @param (geometry: Geometry, srsName: string_좌표계, property?: string_특정 필드이름)
 */
const createPolygonIntersectsFilterData = (geometry: Geometry, srsName: string, property?: string): string => {
    const geomType = geometry.getType();
    let filterParameter = "";
    if (property) {
        filterParameter += `"propertyname":${property},`;
    }
    filterParameter += `
        <ogc:Filter>
          <ogc:Intersects>
            <wfs:PropertyName>XGEOMETRY</wfs:PropertyName>
      `.trim();

    if (geomType === "Polygon") {
        const polygon = geometry as Polygon;
        const coords = polygon.getCoordinates()[0]; // 외곽 링
        const gmlPolygon = createGmlPolygonString(coords);

        filterParameter += `
        <gml:Polygon srsName="${srsName}">
                ${gmlPolygon}
        </gml:Polygon>
        `.trim();
    } else if (geomType === "MultiPolygon") {
        const multiPolygon = geometry as MultiPolygon;
        const polygons = multiPolygon.getPolygons();

        filterParameter += `<gml:MultiPolygon srsName="${srsName}">`;

        polygons.forEach((polygon) => {
            const coords = polygon.getCoordinates()[0];
            const gmlPolygon = createGmlPolygonString(coords);

            filterParameter += `
            <gml:polygonMember>
                <gml:Polygon>
              ${gmlPolygon}
              </gml:Polygon>
            </gml:polygonMember>
          `.trim();
        });

        filterParameter += `</gml:MultiPolygon>`;
    }

    filterParameter += `
          </ogc:Intersects>
        </ogc:Filter>
      `.trim();

    return filterParameter;
};

/*
 * 분석된 결과로 가져온 Isochrone 폴리곤 지도에 그려주는 함수
 * 250908 | smyun | OSM 지도는 폴리곤이 좌표계가 맞지 않아 srs 옵션을 추가하여 좌표계에 맞춰서 transform 하여 폴리곤 그려줌
 */
const addIsochronePolygon = (map: OlMap, polygon: Polygon, srs?: Projection | string) => {
    const layerGroup = getLayerGroup(map, LayerName.DIRECTION);
    if (!layerGroup) return;

    const layer = getLayer(layerGroup, LayerName.ISOCHORNE);
    if (!layer) return;

    // 1) 현재 뷰 좌표계
    const viewCode = map.getView().getProjection().getCode();

    // 2) 폴리곤을 현재 뷰 좌표계로 변환 (clone 사용)
    //    - map srs가 있다면 그걸 기준으로 변환
    let polyToDraw = polygon;

    if (srs && srs !== viewCode) {
        // 폴리곤을 지도 좌표계에 맞게 변환
        polyToDraw = polygon.clone().transform(srs, viewCode) as Polygon; // srs 3857 → viewCode 5179 ..?
    }

    // const source = layer ? (layer.getSource() as VectorSource) : new VectorSource();
    const source = layer.getSource() as VectorSource;
    // 3) 레이어 보이게
    layerGroup.setVisible(true);

    // 4) 소스에 피처 추가
    const feature = new Feature({
        // geometry: polygon,
        geometry: polyToDraw,
    });
    source.addFeature(feature);

    // fit
    map.getView().fit(polyToDraw.getExtent(), {
        padding: [100, 100, 100, 100],
        duration: 500,
    });
};

/**
 *
 * @param map OlMap객체
 * @param activeTool 선택된 분석도구
 * @param stage 분석 단계
 */
const clearLayerByStage = (map: OlMap, activeTool: MapToolType, stage: number) => {
    switch (activeTool) {
        case MapToolType.Direction:
            const layerGroup = getLayerGroup(map, LayerName.DIRECTION);
            if (layerGroup) {
                switch (stage) {
                    case 0:
                        // Layer 전체 초기화
                        clearDirectionLayerGroupAllLayerSource(layerGroup);

                        // 툴팁초기화
                        clearTooltip(map);
                        break;
                    case 1:
                        // polygon Layer 초기화
                        clearPolygonLayerSource(layerGroup);

                        // route Layer 초기화
                        clearRouteLayerSource(layerGroup);

                        // 툴팁초기화
                        clearTooltip(map);
                        //stage 수정
                        break;
                    case 2:
                        // route Layer 초기화
                        clearRouteLayerSource(layerGroup);

                        // 툴팁초기화
                        clearTooltip(map);
                        break;
                }
                break;
            }
    }
};

const clearDirectionLayerGroupAllLayerSource = (layerGroup: LayerGroup) => {
    if (layerGroup) {
        layerGroup.getLayers().forEach((layer) => {
            const source = (layer as Layer).getSource() as VectorSource;
            source.clear();
        });
    }
};

const clearPolygonLayerSource = (layerGroup: LayerGroup) => {
    if (layerGroup) {
        const layer = getLayer(layerGroup, LayerName.ISOCHORNE);
        const source = (layer as Layer).getSource() as VectorSource;
        source.clear();
    }
};

const clearRouteLayerSource = (layerGroup: LayerGroup) => {
    if (layerGroup) {
        const layer = getLayer(layerGroup, LayerName.ROUTE);
        const source = (layer as Layer).getSource() as VectorSource;
        source.clear();
    }
};

const stopDirection = (map: OlMap) => {
    const directionInteraction = map
        .getInteractions()
        .getArray()
        .filter((interaction) => interaction.get("gName") == LayerName.DIRECTION)[0];

    // interaction remove
    if (directionInteraction) {
        map.removeInteraction(directionInteraction);
    }

    // layer Control
    const layerGroup = getLayerGroup(map, LayerName.DIRECTION);
    if (layerGroup) {
        // Source Clear
        clearDirectionLayerGroupAllLayerSource(layerGroup);
        layerGroup.setVisible(false);
    }

    // 툴팁 제거
    clearTooltip(map);
};

// bubblemap --------------------------------------------
/**
 * 버블맵 style 함수
 * @returns style
 */

// let _clusterLayerStyleCache = {} as Style;
const getClusterStyle = (feature: Feature<Geometry>) => {
    // clusterLSC에 값이 있다면 style에 이용
    const size = feature.get("features").length as number;
    // let style = _clusterLayerStyleCache[size];
    let style;

    if (!style) {
        const radius = getRadius(size);
        const color = getColor(size);
        // style = _clusterLayerStyleCache[size] = new Style({
        style = new Style({
            image: new CircleStyle({
                radius: radius,
                stroke: new Stroke({
                    color: "rgba(0,0,0,1)",
                    width: 1,
                }),
                fill: new Fill({
                    color: "rgba(" + color + ",0.7)",
                }),
            }),
            text: new Text({
                // 25.07.09 | Kim Young Jun | 1,000 단위 구분자
                text: formatNumber(size),
                fill: new Fill({
                    color: "#fff",
                }),
            }),
        });
    }
    return style as StyleLike;
};

/**
 * 버블맵 style용 버블 반경계산 함수
 * @returns result(radius size)
 */
function getRadius(size: number) {
    let result = 0;
    // 9단계로 구분
    if (size > 0 && size <= 4) {
        result = 6;
    } else if (size > 4 && size <= 9) {
        result = 10;
    } else if (size > 9 && size <= 20) {
        result = 15;
    } else if (size > 20 && size <= 50) {
        result = 20;
    } else if (size > 50 && size <= 80) {
        result = 25;
    } else if (size > 80 && size <= 119) {
        result = 30;
    } else if (size > 119 && size <= 555) {
        result = 35;
    } else if (size > 555 && size <= 3000) {
        result = 40;
    } else {
        result = 45;
    }

    return result;
}
/**
 * 버블맵 style용 반경에 따른 색상 함수
 * @returns result(색상코드)
 */

function getColor(size: number) {
    let result = "";

    if (size > 0 && size <= 5) {
        result = "100, 100, 229";
    } else if (size > 5 && size <= 30) {
        result = "50, 50, 200";
    } else {
        result = "0, 0, 200";
    }
    return result;
}

/**
 * 범위분석 관련 함수
 */
// 생성된 폴리곤 꼭짓점 좌표 값 함수
function getPolygonVerticesFromGeometry(polygon: Polygon): Coordinate[][] {
    if (polygon.getLinearRingCount() < 2) return [];

    const coordinates: Coordinate[][] = []; // 결과를 담을 배열

    // 내부 링이 존재하면
    const innerRingCount = polygon.getLinearRingCount();
    for (let i = 1; i < innerRingCount; i++) {
        // 1번부터 끝까지 내부 링
        const innerRing = polygon.getLinearRing(i);
        coordinates.push(innerRing!.getCoordinates().slice(0, -1)); // 내부 링의 좌표 추가
    }

    return coordinates;
}

export interface LegendClass {
    class: number;
    min: number;
    max: number;
}

/**
 * 범례 연산
 * 수정 필요
 * 25.07.08 | Kim Young Jun | 범례 계산 로직 개선 진행
 */
function generateJenksLegend(values: number[], forcedClassCount?: number): LegendClass[] {
    if (!values || values.length < 2) return [];

    // 중복 제거 및 정렬
    const uniqueValues = Array.from(new Set(values)).sort((a, b) => a - b);

    // 고유 값 개수에 따라 클래스 수 결정
    let desiredClassCount: number;

    if (forcedClassCount) {
        // 강제 클래스 수가 있으면 그것을 사용
        desiredClassCount = forcedClassCount;
    } else {
        // 고유 값의 개수에 따라 적절한 클래스 수 결정
        const numOfValues = uniqueValues.length;
        if (numOfValues <= 3) {
            desiredClassCount = numOfValues;
        } else if (numOfValues <= 6) {
            desiredClassCount = 3;
        } else if (numOfValues <= 10) {
            desiredClassCount = 4;
        } else if (numOfValues <= 20) {
            desiredClassCount = 5;
        } else {
            desiredClassCount = 6;
        }
    }

    // 최소 클래스 수는 1, 최대는 고유 값의 개수
    const finalClassCount = Math.min(Math.max(1, desiredClassCount), uniqueValues.length);

    // Jenks 알고리즘으로 구간 나누기
    const breaks = jenks(uniqueValues, finalClassCount);

    // breaks에 0이 있다면 제거
    if (breaks[0] === 0) {
        breaks.shift(); // 첫 번째 값이 0이면 제거
    }

    // 범례 클래스 생성
    const legends: LegendClass[] = [];

    // 모든 범위 구간에 대해 범례 클래스 생성
    for (let i = 0; i < breaks.length - 1; i++) {
        legends.push({
            class: i + 1, // 클래스 번호는 1부터 시작
            min: breaks[i],
            max: breaks[i + 1],
        });
    }

    // 마지막 구간의 최댓값이 실제 데이터의 최댓값과 일치하는지 확인
    const actualMax = Math.max(...values);
    const last = legends[legends.length - 1];

    if (last && last.max > actualMax) {
        last.max = actualMax; // 최댓값 보정
    }

    return legends;
}
/**
 * 범례 값 분류 함수
 * @param value - 분류할 값
 * @param legend - 범례 클래스 배열
 * @returns 해당 값이 속하는 클래스 번호
 */
function classifyValue(value: number, legend: LegendClass[]): number {
    // 범례가 비어있거나 값이 NaN이면 0 반환
    if (!legend || legend.length === 0 || isNaN(value)) {
        return 0;
    }

    // 모든 값이 같은 경우를 처리 (첫 번째 범례의 min과 max가 같을 때)
    if (legend.length === 1 && legend[0].min === legend[0].max) {
        return value === legend[0].min ? 1 : 0;
    }

    // 범위에 맞는 클래스 찾기
    for (const cls of legend) {
        // 일반적인 범위 체크
        if (cls.min <= value && value < cls.max) {
            return cls.class;
        }

        // 마지막 클래스의 최대값은 포함
        if (cls.class === legend.length && value === cls.max) {
            return cls.class;
        }

        // min과 max가 동일한 경우(단일값) 처리
        if (cls.min === cls.max && value === cls.min) {
            return cls.class;
        }
    }

    // 범례의 전체 범위 확인
    const minRange = Math.min(...legend.map((c) => c.min));
    const maxRange = Math.max(...legend.map((c) => c.max));

    // 범례 범위 밖의 값 처리
    if (value < minRange) {
        return 1; // 최소값보다 작으면 첫 번째 클래스로
    } else if (value >= maxRange) {
        return legend.length; // 최대값보다 크거나 같으면 마지막 클래스로
    }

    // 그 외의 경우는 0 반환
    return 0;
}

export {
    CalToolType,
    RegionType,
    ColorPreset,
    accessibilityCondition,
    extentAnalysisStyle,
    extentAnalysisPolyStyle,
    extentAnalysisCircleStyle,
    extentAnalysisOutsideStyle,
    blueCircleStyle,
    accessibilityPolygonStyle,
    accessibilityRawPolygonStyle,
    accessibilityMarkerStyle,
    createRegionLayer,
    createChartRegionLayer,
    createDirectionLayer,
    getClusterStyle,
    getRegionType,
    getUpperRegionCode,
    choroplethStyleFunction,
    chartStyleFunction,
    directionStyleFunction,
    getCoordinatesFromIsochrone,
    createPolygonIntersectsFilterData,
    regionStyleFunction,
    updateOverlayLabel,
    updateOverlayChartLabel,
    getColorByClass,
    startMeasure,
    stopMeasure,
    stopAccessibility,
    stopOverlapMap,
    // startDirection,
    addIsochronePolygon,
    clearLayerByStage,
    stopDirection,
    colorMaker,
    drawRouteMultiLine,
    clearTooltip,
    getPolygonVerticesFromGeometry,
    getCircleRadius,
    generateJenksLegend,
    classifyValue,
    formatLength,
    formatNumber,
};
